export default function Pricing() {
  const plans = [
    {
      name: 'ライトプラン',
      price: '¥39,800',
      priceNote: '〜',
      subtitle: '最初の一歩',
      description: '「まずは整理したい」「相談できる相手がほしい」企業向けのプランです。',
      features: [
        'DXに関する相談・質問対応（チャット／オンライン）',
        '現状や課題の整理サポート',
        '情報収集・ツール選定に関するアドバイス',
        '次にやるべきことの方向性整理'
      ],
      summary: 'DXに取り組む前の「考える段階」を一人で抱え込まないためのプラン'
    },
    {
      name: 'スタンダードプラン',
      price: '¥148,000',
      priceNote: '〜',
      subtitle: '実行フェーズ',
      description: '整理だけでなく、実際に進めたい企業向けのプランです。',
      features: [
        '定期的な打ち合わせによる伴走支援',
        'DXの進め方・手順の設計',
        '採用・集客・業務改善に関する具体施策の検討',
        '実装・運用に向けたサポート',
        '実行後の振り返り・改善提案'
      ],
      summary: '「何をやるか」だけでなく、「どう進めて、どう回すか」まで一緒に考えます。',
      recommended: true
    },
    {
      name: 'プレミアムプラン',
      price: '¥298,000',
      priceNote: '〜',
      subtitle: '全社DX',
      description: '複数の課題を同時に扱い、全体を見ながらDXを進めたい企業向けのプランです。',
      features: [
        '全体設計を踏まえたDX推進サポート',
        '採用・集客・業務改善を横断した支援',
        '社内体制や役割分担を含めた整理',
        '継続的な改善・見直しの伴走',
        '社内にノウハウを残すための支援'
      ],
      summary: '一部の改善ではなく、会社全体の進め方を整えたい場合に適しています。'
    }
  ];

  const planDetails = [
    {
      title: '月額の伴走支援で対応する内容',
      description: '月額プランでは、DXを継続して進めるための整理・設計・判断支援を行います。日々の検討や改善を一緒に進めていくことが前提です。',
      items: [
        '採用DX・集客DX・業務改善DXの進め方整理',
        '施策の優先順位付けや方向性の判断',
        '実行後の振り返りや改善の検討'
      ],
      icon: 'ri-compass-3-line',
      color: '#12335f'
    },
    {
      title: 'スポット型で対応する内容',
      description: '一方で、WebリニューアルやLP制作など、制作物が明確なものについては、スポット型で対応します。',
      items: [
        'Webサイトのリニューアル',
        '採用・集客用のLP制作',
        '導線設計やページ構成の見直し'
      ],
      note: 'これらは月額に含めず、必要なタイミングで、必要な範囲だけ対応します。',
      icon: 'ri-flashlight-line',
      color: '#037555'
    },
    {
      title: '他サービスとの組み合わせについて',
      description: '取り組む内容によっては、デジポップブーストに加え、以下のサービスを組み合わせて対応します。',
      items: [
        'ウェブポップブースト（Webマーケティング支援）',
        'ソーシャルポップブースト（SNS運用支援）',
        'アイポップブースト（生成AI活用支援）'
      ],
      note: 'すべてを月額にまとめるのではなく、伴走支援とスポット対応を使い分けることで、無理のない形でDXを進めます。',
      icon: 'ri-puzzle-line',
      color: '#ea5614'
    }
  ];

  return (
    <section id="price" className="py-24 lg:py-32 bg-white relative overflow-hidden">
      {/* 背景装飾 */}
      <div className="absolute top-20 left-10 w-96 h-96 bg-[#12335f]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-40 right-10 w-80 h-80 bg-[#ea5614]/5 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        <div className="text-center mb-20">
          <h2 className="text-3xl lg:text-4xl font-bold text-[#12335f] mb-6">
            料金プラン（月額・伴走型）
          </h2>
          <p className="text-base lg:text-lg text-gray-700 max-w-3xl mx-auto">
            デジポップブーストは、一度きりの支援ではなく、<strong>状況を見ながら一緒に進める月額制のDX支援</strong>です。<br />
            目的や社内体制に合わせて、プランを選べます。
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-24">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`rounded-2xl p-8 transition-all hover:scale-105 ${
                plan.recommended
                  ? 'bg-gradient-to-br from-[#12335f] to-[#153577] text-white shadow-2xl relative'
                  : 'bg-white text-gray-900 shadow-lg border border-gray-100'
              }`}
            >
              {plan.recommended && (
                <>
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-2xl"></div>
                  <div className="text-center mb-4 relative z-10">
                    <span className="inline-block px-4 py-1 bg-[#ea5614] text-white text-xs font-medium rounded-full shadow-md">
                      おすすめ
                    </span>
                  </div>
                </>
              )}
              <h3
                className={`text-xl lg:text-2xl font-bold mb-1 ${
                  plan.recommended ? 'text-white' : 'text-[#12335f]'
                }`}
              >
                {plan.name}
              </h3>
              <div
                className={`text-sm mb-4 ${
                  plan.recommended ? 'text-white/70' : 'text-gray-600'
                }`}
              >
                {plan.subtitle}
              </div>
              <div className="mb-4">
                <span className="text-3xl font-bold">{plan.price}</span>
                <span
                  className={`text-lg ml-1 ${
                    plan.recommended ? 'text-white/80' : 'text-gray-600'
                  }`}
                >
                  {plan.priceNote}
                </span>
              </div>
              <p
                className={`text-sm mb-6 leading-relaxed ${
                  plan.recommended ? 'text-white/90' : 'text-gray-700'
                }`}
              >
                {plan.description}
              </p>
              <div className="space-y-3 mb-6">
                {plan.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                      <i
                        className={`ri-check-line text-lg ${
                          plan.recommended ? 'text-[#037555]' : 'text-[#037555]'
                        }`}
                      ></i>
                    </div>
                    <span
                      className={`text-sm ml-2 ${
                        plan.recommended ? 'text-white/90' : 'text-gray-700'
                      }`}
                    >
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
              <p
                className={`text-sm mb-8 leading-relaxed font-medium ${
                  plan.recommended ? 'text-white' : 'text-[#153577]'
                }`}
              >
                {plan.summary}
              </p>
              <a
                href="#contact"
                className={`block w-full py-3 text-center text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                  plan.recommended
                    ? 'bg-[#9a1e23] text-white hover:bg-[#7d181c] shadow-md hover:shadow-lg'
                    : 'bg-[#12335f] text-white hover:bg-[#153577]'
                }`}
              >
                詳しく見る
              </a>
            </div>
          ))}
        </div>

        <div className="mb-20">
          <h3 className="text-2xl lg:text-3xl font-bold text-[#12335f] mb-8 text-center">
            プランに含まれる内容と、追加・スポット対応について
          </h3>
          <p className="text-base text-gray-700 mb-12 text-center max-w-3xl mx-auto">
            DXの進め方は、企業ごとに異なります。<br />
            デジポップブーストでは、月額の伴走支援を軸にしながら、内容に応じて、スポット型の支援も組み合わせて対応しています。
          </p>

          <div className="max-w-3xl mx-auto space-y-10">
            <div>
              <h4 className="text-lg font-bold text-[#12335f] mb-3">
                ■ 月額の伴走支援で対応する内容
              </h4>
              <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                月額プランでは、DXを継続して進めるための整理・設計・判断支援を行います。日々の検討や改善を一緒に進めていくことが前提です。
              </p>
              <ul className="text-sm text-gray-700 space-y-1 pl-4">
                <li>・採用DX・集客DX・業務改善DXの進め方整理</li>
                <li>・施策の優先順位付けや方向性の判断</li>
                <li>・実行後の振り返りや改善の検討</li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold text-[#12335f] mb-3">
                ■ スポット型で対応する内容
              </h4>
              <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                一方で、WebリニューアルやLP制作など、制作物が明確なものについては、スポット型で対応します。
              </p>
              <ul className="text-sm text-gray-700 space-y-1 pl-4">
                <li>・Webサイトのリニューアル</li>
                <li>・採用・集客用のLP制作</li>
                <li>・導線設計やページ構成の見直し</li>
              </ul>
              <p className="text-sm text-[#153577] mt-3 font-medium">
                ※ これらは月額に含めず、必要なタイミングで、必要な範囲だけ対応します。
              </p>
              <a
                href="https://mgmtsys.jdnw.jp/4880/hp"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-4 px-4 py-2 text-xs font-medium text-[#12335f] border border-[#12335f] rounded-md hover:bg-[#12335f] hover:text-white transition-all cursor-pointer whitespace-nowrap"
              >
                詳しくはこちらへ
              </a>
            </div>

            <div>
              <h4 className="text-lg font-bold text-[#12335f] mb-3">
                ■ 他サービスとの組み合わせについて
              </h4>
              <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                取り組む内容によっては、デジポップブーストに加え、以下のサービスを組み合わせて対応します。
              </p>
              <ul className="text-sm text-gray-700 space-y-1 pl-4">
                <li>
                  ・<a href="https://webpop.in/" target="_blank" rel="noopener noreferrer" className="text-[#12335f] hover:text-[#9a1e23] transition-colors cursor-pointer">ウェブポップブースト（Webマーケティング支援）</a>
                </li>
                <li>
                  ・<a href="https://digipop.co.jp/product/socialpop/" target="_blank" rel="noopener noreferrer" className="text-[#12335f] hover:text-[#9a1e23] transition-colors cursor-pointer">ソーシャルポップブースト（SNS運用支援）</a>
                </li>
                <li>
                  ・<a href="https://digipop.co.jp/product/aipop/" target="_blank" rel="noopener noreferrer" className="text-[#12335f] hover:text-[#9a1e23] transition-colors cursor-pointer">アイポップブースト（生成AI活用支援）</a>
                </li>
              </ul>
              <p className="text-sm text-[#153577] mt-3 font-medium">
                ※ すべてを月額にまとめるのではなく、伴走支援とスポット対応を使い分けることで、無理のない形でDXを進めます。
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-[#12335f] to-[#153577] rounded-2xl p-8 lg:p-12 text-center mb-4 shadow-lg relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>
          <div className="relative z-10">
            <p className="text-base lg:text-lg text-white/90 mb-6 leading-relaxed">
              どのプランや組み合わせが合っているかは、状況によって変わります。<br />
              無料DX推進診断では、今の課題と体制を確認した上で、最適な進め方をご提案します。
            </p>
            <p className="text-lg lg:text-xl text-white font-bold mb-6">
              まずは無料DX推進診断で、<br />
              今の課題と、無理のない進め方をご確認ください。
            </p>
            <a
              href="https://digipop.co.jp/webservice/dx-diagnosis/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-8 py-3 bg-[#9a1e23] text-white text-sm font-medium rounded-lg hover:bg-[#7d181c] transition-all cursor-pointer whitespace-nowrap shadow-md hover:shadow-lg"
            >
              無料診断を申し込む
            </a>
          </div>
        </div>

        <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 text-center shadow-sm">
          <p className="text-base text-gray-700 leading-relaxed">
            無理な売り込みは行いません。<br />
            まずは現状を整理し、やるべきこと・やらなくていいことを明確にさせていただきます。
          </p>
        </div>
      </div>
    </section>
  );
}
